import React, { FC, useEffect, useMemo, useState } from 'react';

import { Typography, useTheme } from '@mui/material';
import clsx from 'clsx';
import 'swiper/swiper.min.css';
import { useTranslation } from 'react-i18next';

import Api from '~/Api';
import Loader from '~/components/atoms/Loader';
import {
  PaymentApi,
  UpdatePaymentTokenReason,
} from '~/components/organisms/SolidCheckout/types';
import { useIsViewport, Viewports } from '~/hooks/useIsViewport';
import PolyllmRedemptionCheckout from '~/projects/polyllm-chat/components/RedemptionCheckout';
import { ProjectBehaviorStrategy } from '~/projects/ProjectBehaviorStrategy';
import analytics from '~/services/analytics';
import { Typographies } from '~/theme/typography';
import { AnalyticPaymentMethods, PAYMENT_METHODS } from '~/types/payment';

import styles from './styles.module.scss';
import PaymentFormV1 from '../components/PaymentForm/V1';
import Agreement from '../components/PaymentForm/V1/Agreement';
import Guarantee from '../components/PaymentForm/V1/Guarantee';
import PaymentInfo from '../components/PaymentForm/V1/PaymentInfo';
import PaymentSystems from '../components/PaymentForm/V1/PaymentSystems';
import UpdatePaymentDescription from '../components/PaymentForm/V1/UpdatePaymentDescription';

type Props = {
  onSuccessPayment: () => void | Promise<void>;
};

const CheckoutUpdateToken: FC<Props> = ({ onSuccessPayment }) => {
  const { t } = useTranslation('general');
  const theme = useTheme();

  const [isFormShown, setIsFormShown] = useState(false);

  const isDesktop = useIsViewport(Viewports.EXTRA_SMALL_DESKTOP);

  const handlePaymentSuccess = async (): Promise<void> => {
    analytics.trackEvent('platform checkout - payment update success');
    await onSuccessPayment();
  };

  const handlePaymentFail = (
    message: string,
    method: AnalyticPaymentMethods,
  ): void => {
    analytics.trackEvent('platform checkout - error', {
      method,
      error: message,
    });
  };

  const handleCheckoutSubmit = (method: AnalyticPaymentMethods): void => {
    analytics.trackEvent('platform checkout - submit', {
      method,
    });
  };

  const handlePaymentMethodChange = (type: PAYMENT_METHODS): void => {
    analytics.trackEvent('platform checkout - navigation', {
      method: type,
    });
  };

  useEffect(() => {
    analytics.trackEvent('platform checkout - payment update view');
  }, []);

  const paymentApi: PaymentApi = useMemo(
    () => ({
      createPaymentIntentCard: (geo_country) =>
        Api.updatePaymentMethodCard({
          geo_country,
          token_type: UpdatePaymentTokenReason.REDEMPTION,
        }),
      createPaymentIntentPaypal: () => Api.updatePaymentMethodPaypal(),
    }),
    [],
  );

  if (ProjectBehaviorStrategy.usesPolyllmRedemptionCheckout) {
    return (
      <PolyllmRedemptionCheckout
        onSuccessPayment={onSuccessPayment}
        isDark={theme.palette.mode === 'dark'}
      />
    );
  }

  const PaymentFormComponent = PaymentFormV1;

  return (
    <>
      {!isFormShown && <Loader />}

      <div className={styles.outer_wrapper}>
        <div
          className={clsx(styles.inner_wrapper, {
            [styles.is_shown]: isFormShown,
          })}
        >
          {isDesktop && (
            <div className={styles.description}>
              <UpdatePaymentDescription />
            </div>
          )}

          <div className={styles.form_outer}>
            <div className={styles.form_inner}>
              <Typography
                className={styles.title}
                variant={Typographies.LABEL_LARGE}
                component="h3"
              >
                {t(
                  'subscription:payment_form.select_payment_method',
                  'Select your payment method',
                )}
              </Typography>

              <PaymentFormComponent
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentFail={handlePaymentFail}
                onPaymentSubmit={handleCheckoutSubmit}
                onPaymentMethodChange={handlePaymentMethodChange}
                onFormReady={() => setIsFormShown(true)}
                paymentApi={paymentApi}
              />

              <Guarantee />

              <PaymentInfo
                price="0"
                currency="USD"
                title={t(
                  'subscription:payment_form.payment_method_update',
                  'Payment Method Update',
                )}
                description={t(
                  'subscription:payment_form.payment_method_update_desc',
                  'Updated method will be used for billing your existing plan',
                )}
              />

              <PaymentSystems />
            </div>

            <Agreement />
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutUpdateToken;
